/**
 * Multi-step guest-first RSVP.
 * Steps: 1) Essentials  2) About You  3) Intentions  4) Photos + Optional Details
 *        5) Payment placeholder  6) Confirmation + optional account
 * Progress bar reuses onboarding pattern. Reads `required_registration_fields`
 * from the event to compute per-step validation.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, ArrowRight, Check, Sparkles, Upload, X, ShieldCheck,
  Mail, Phone, MapPin, Heart, Users, Camera, CreditCard, Send,
} from 'lucide-react';
import api from '@/utils/api';
import { extractErrorMessage } from '@/utils/errorMessage';
import { useAuth } from '@/contexts/AuthContext';
import { usePixels } from '@/contexts/PixelContext';
import DostPolishModal from '@/components/dost/DostPolishModal';
import PaymentStep from '@/components/rsvp/PaymentStep';
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from '@/components/ui/tooltip';

const GENDERS = [
  // C.1c: canonical values are ONLY male / female.
  { v: 'male', label: 'Male' },
  { v: 'female', label: 'Female' },
];

const INTERESTS_FALLBACK = [];
const ACTIVITIES_FALLBACK = [];
import { INTERESTS as INT_LIST_FALLBACK, ACTIVITIES as ACT_LIST_FALLBACK } from '@/data/interests';
import { LANGUAGES as LANG_LIST } from '@/data/languages';
import { COUNTRIES, CITIES } from '@/data/cities';

const INTENTIONS = [
  { v: 'friendship', label: 'Friendship', desc: 'Genuine platonic connections' },
  { v: 'community', label: 'Community', desc: 'Being part of an ongoing circle' },
  { v: 'marriage', label: 'Potential Marriage Partner', desc: 'Open to serious introductions' },
  { v: 'travel_buddies', label: 'Travel Buddies', desc: 'Companions for future trips' },
];

const TOTAL_STEPS = 6;

const RsvpMultiStepPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  // C.4: authenticated-user detection. Auth users get a top banner,
  // profile prefill, and the source-attribution question is skipped.
  const { user: authUser, logout } = useAuth();
  const { trackEvent } = usePixels();
  const isAuthenticated = !!authUser;

  const [step, setStep] = useState(1);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Bundle 154 - once the user attempts to advance from step 4
  // without satisfying the photo + social mandate, we surface
  // inline error badges on the offending groups. Reset back to
  // false whenever the user returns to a prior step.
  const [showMandateHint, setShowMandateHint] = useState(false);
  const [resultUrls, setResultUrls] = useState(null); // { edit_url, verify_url_fallback, verify_sent }
  // Bundle 176 - Task 3. Retry Payment affordance state.
  const [showRetryPaymentModal, setShowRetryPaymentModal] = useState(false);
  const [retryPaymentError, setRetryPaymentError] = useState('');
  const [retryPaymentLoading, setRetryPaymentLoading] = useState(false);

  // Bundle 55 - Bug 1 hardening. RsvpMultiStepPage MUST be gated
  // behind auth. Bundle 51 added the AuthGateModal on the event
  // detail page's RSVP button, but a determined user (or an old
  // bookmark) can still land directly on `/events/:slug/rsvp` and
  // walk through the form. Backend 401s the submit, but the FE
  // should not waste the user's time - kick them back to the event
  // page which pops the auth modal automatically via `?rsvp=1`.
  useEffect(() => {
    if (!slug) return;
    if (typeof window === 'undefined') return;
    if (authUser === undefined) return; // AuthContext still loading
    if (!authUser) {
      navigate(`/events/${slug}?rsvp=1`, { replace: true });
    }
  }, [slug, authUser, navigate]);
  const [uploadSession, setUploadSession] = useState(null);
  const [taxo, setTaxo] = useState({ interests: [], activities: [] });
  const [dostOpen, setDostOpen] = useState(false);
  // Bug Z3: resume-from-refresh support. Storage is scoped by event so
  // switching events doesn't leak state, and drafts older than 7 days
  // are ignored. Never persists the payment step.
  const [resumedDraft, setResumedDraft] = useState(false);
  const draftKey = React.useMemo(
    () => event?.id ? `dostination.rsvp_draft.${event.id}` : null,
    [event?.id],
  );

  const [f, setF] = useState({
    full_name: '', preferred_first_name: '', dob_or_age: '',
    gender: '', city: '', state_region: '', country: 'United States',
    email: '', phone: '',
    about_me: '', languages: [], occupation: '',
    interests: [], activity_interests: [],
    intentions: [], marriage_prefs: { age_min: '', age_max: '', notes: '' },
    curated_seating_consent: false, organizer_intro_consent: false,
    photos: [],
    dietary_restrictions: '', food_allergies: '',
    accessibility_request: '',
    social_handles: { instagram: '', linkedin: '', tiktok: '', facebook: '' },
    min_age_confirmation: false,
    // Batch 4: "How did you hear about this event?" required for all
    // RSVPs (guest and authenticated). This replaced the legacy C.4
    // `source_channel` question - the legacy field is no longer written
    // by new submissions but historical rows retain their value.
    heard_from: '',
    heard_from_other: '',
    // Bundle 144 - mandatory Event Terms & Conditions checkbox on
    // the final pre-submit step. Backend rejects submits without
    // this flag with 400 code TERMS_REQUIRED.
    terms_accepted: false,
  });

  // Bundle 176 - Task 2. Defensive server-truth hydration.
  // When we have a registration id (post-submit or from URL param),
  // fetch the wizard-state from the server and reconcile the step +
  // sub-panel BEFORE trusting client state. Prevents future payment
  // loops caused by client/server state drift.
  useEffect(() => {
    const rid = resultUrls?.registration_id;
    if (!rid) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/rsvp/${rid}/wizard-state`);
        if (cancelled || !data) return;
        const state = data.payment_confirmation_state;
        setResultUrls((prev) => (prev ? {
          ...prev,
          payment_confirmation_state: state,
          duplicate: (data.current_step === 'confirmation'
                       && ['verified', 'pending_verify',
                            'free', 'waived'].includes(state)),
          next_step: data.current_step,
          // Bundle 177 - Payment-first Step 6. Carry the new fields
          // so Step 6 can render the "Complete your profile" block.
          profile_action_required: !!data.profile_action_required,
          resume_profile_step: data.resume_profile_step || 6,
        } : prev));
        if (data.current_step === 'confirmation') setStep(6);
        else if (data.current_step === 'payment') setStep(5);
      } catch (_) {
        // Non-fatal - fall back to whatever the submit response said.
      }
    })();
    return () => { cancelled = true; };
  }, [resultUrls?.registration_id]);

  // Bug Z3: rehydrate on mount (once event id is known). Ignore drafts >7d.
  useEffect(() => {
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed?.savedAt) return;
      const ageMs = Date.now() - parsed.savedAt;
      if (ageMs > 7 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(draftKey);
        return;
      }
      if (parsed.form && typeof parsed.form === 'object') {
        setF((prev) => ({ ...prev, ...parsed.form }));
        // Zelle 422 hotfix: cap the restored step at 4. Step 5 (payment
        // chooser) needs the post-submit `resultUrls` (registration_id +
        // edit_token) which lives in ephemeral React state and CANNOT be
        // persisted. Restoring step 5 without those values renders
        // PaymentStep with undefined ids, causing a FastAPI 422 "Field
        // required" toast on the first Zelle click. Snap back to the last
        // pre-submit step so the guest re-submits (dedup handles the
        // "already RSVP'd" branch cleanly).
        setStep(Math.max(1, Math.min(parsed.step || 1, 4)));
        setResumedDraft(true);
      }
    } catch { /* corrupt draft - ignore */ }
  }, [draftKey]);

  // Bug Z3: persist on every form / step change (throttled by React
  // batching). Payment step (step 5) and confirmation (step 6) are
  // intentionally excluded because they depend on ephemeral post-submit
  // state (`resultUrls`) that we do NOT persist - restoring step 5
  // alone triggers the FastAPI 422 "Field required" bug on Zelle clicks
  // (see draft-restore comment above).
  useEffect(() => {
    if (!draftKey || step >= 5) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        form: f, step, savedAt: Date.now(),
      }));
    } catch { /* quota exceeded etc. - non-fatal */ }
  }, [draftKey, f, step]);

  // Bug Z3: push a browser history entry per wizard step so the native
  // Back button walks back through steps instead of dumping the guest off
  // the RSVP page mid-flow. We only rewrite state (no URL change) so
  // deep links still work. Confirmation step (6) is terminal.
  useEffect(() => {
    if (!event || step >= 6) return;
    try {
      window.history.pushState({ rsvpStep: step, slug }, '');
    } catch { /* ignore */ }
  }, [step, event, slug]);

  useEffect(() => {
    const onPop = (e) => {
      const s = e.state?.rsvpStep;
      if (typeof s === 'number' && s >= 1 && s <= 5) {
        setStep(s);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const clearDraft = React.useCallback(() => {
    if (!draftKey) return;
    try { localStorage.removeItem(draftKey); } catch { /* noop */ }
    setResumedDraft(false);
  }, [draftKey]);

  const startOver = () => {
    if (!window.confirm('Discard your saved draft and start over?')) return;
    clearDraft();
    setF({
      full_name: '', preferred_first_name: '', dob_or_age: '',
      gender: '', city: '', state_region: '', country: 'United States',
      email: '', phone: '',
      about_me: '', languages: [], occupation: '',
      interests: [], activity_interests: [],
      intentions: [], marriage_prefs: { age_min: '', age_max: '', notes: '' },
      curated_seating_consent: false, organizer_intro_consent: false,
      photos: [],
      dietary_restrictions: '', food_allergies: '',
      accessibility_request: '',
      social_handles: { instagram: '', linkedin: '', tiktok: '', facebook: '' },
      min_age_confirmation: false,
      heard_from: '',
      heard_from_other: '',
      terms_accepted: false,
    });
    setStep(1);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/events/slug/${slug}`);
        setEvent(data.event);
        // Bundle 63 - fire `RsvpStart` the moment the wizard resolves
        // an event. Runs once per event per session (`sessionStorage`
        // guard) so a mid-flow refresh doesn't double-fire. Previous
        // wiring only fired `Lead` on submit, which under-counted the
        // "started but did not finish" cohort (369 view_content -> 6
        // rsvp_start on the Feb 2026 audit). Fires client fbq + server
        // CAPI mirror + Advanced Matching automatically via trackEvent.
        try {
          const evId = data.event?.id || slug || '';
          const skey = `dostination.rsvp_start_fired.${evId}`;
          if (typeof window !== 'undefined'
              && !window.sessionStorage.getItem(skey)) {
            window.sessionStorage.setItem(skey, '1');
            const md = {
              email: authUser?.email,
              phone: authUser?.phone,
              first_name: authUser?.first_name,
              last_name: authUser?.last_name,
            };
            trackEvent('RsvpStart', {
              content_name: data.event?.title || 'event',
              content_ids: [String(data.event?.slug || slug || evId)],
              content_category: 'rsvp',
              content_type: 'event_ticket',
            }, md);
          }
        } catch (_) { /* never break the flow */ }
        // Mint an upload-session token so guests can attach photos in Step 4.
        try {
          const { data: sess } = await api.post('/rsvp/upload-session', { slug });
          setUploadSession(sess.upload_token);
        } catch (_) { /* graceful - photos step will show a hint */ }
        // Fetch taxonomies scoped to this event (event override honored if set)
        try {
          const [ints, acts] = await Promise.all([
            api.get(`/taxonomies/interests?event_slug=${encodeURIComponent(slug)}`),
            api.get(`/taxonomies/activities?event_slug=${encodeURIComponent(slug)}`),
          ]);
          setTaxo({
            interests: (ints.data.items || []).map((x) => x.label),
            activities: (acts.data.items || []).map((x) => x.label),
          });
        } catch (err) {
          // Non-fatal: chip groups will fall back to the static list.
          console.warn('Taxonomy fetch failed, using fallback list', err);
        }
      } catch (e) {
        setError('Event not available.');
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const intList = taxo.interests.length ? taxo.interests : INT_LIST_FALLBACK;
  const actList = taxo.activities.length ? taxo.activities : ACT_LIST_FALLBACK;

  const minAge = event?.minimum_age || 21;

  const toggleChip = (arr, v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  // Bundle 154 - derived flag for the "at least 1 social" check.
  // Reused by both the inline hint block AND the case-4 gate below.
  const anySocialFilled = useMemo(() => {
    const sh = f.social_handles || {};
    return ['instagram', 'linkedin', 'tiktok', 'facebook']
      .some((k) => (sh[k] || '').trim().length > 0);
  }, [f.social_handles]);

  const stepIsValid = useMemo(() => {
    switch (step) {
      case 1: {
        const baseValid = f.full_name && f.preferred_first_name && f.dob_or_age && f.gender
          && f.city && f.state_region && f.country && f.email && f.phone && f.min_age_confirmation;
        if (!baseValid) return false;
        // Bundle 160 - preemptive last-name check. Backend Bundle
        // 149 rejects RSVPs where `full_name` does not split into
        // a first + last with `LAST_NAME_REQUIRED`. Users typing
        // a mononym / single word previously walked all the way
        // to step 4 (T&C) and hit a generic error on submit. Gate
        // it here so the fix is clear + local to the offending
        // field. Trimmed full_name must contain at least one
        // space AND both segments must be >= 2 chars.
        const parts = String(f.full_name || '').trim().split(/\s+/);
        if (parts.length < 2) return false;
        if (parts[0].length < 2) return false;
        if (parts[parts.length - 1].length < 2) return false;
        // Batch 4: heard_from is required for every RSVP (guest + auth).
        // "Other" requires a non-empty free-text field.
        if (!f.heard_from) return false;
        if (f.heard_from === 'other' && !(f.heard_from_other || '').trim()) return false;
        return true;
      }
      case 2:
        return !!(f.about_me
          && (f.about_me || '').trim().length >= 30
          && (f.occupation || '').trim().length > 0
          && f.languages.length > 0
          && f.interests.length > 0
          && f.activity_interests.length > 0);
      case 3:
        return f.intentions.length > 0;
      case 4: {
        // Bundle 144 - Event T&C must be checked on the final
        // pre-submit step. Bundle 154 - additionally require at
        // least 1 profile picture AND at least 1 non-empty social
        // handle. The dietary / allergy / accessibility fields
        // remain optional.
        if (!f.terms_accepted) return false;
        if (!Array.isArray(f.photos) || f.photos.length < 1) return false;
        const sh = f.social_handles || {};
        const anySocial = ['instagram', 'linkedin', 'tiktok', 'facebook']
          .some((k) => (sh[k] || '').trim().length > 0);
        if (!anySocial) return false;
        return true;
      }
      case 5:
        return true;
      default: return true;
    }
  }, [step, f, isAuthenticated]);

  // Bundle 162 - per-step "why is Continue disabled" hint. When
  // stepIsValid is false, we compute the exact missing fields
  // so we can surface them inline. Prior RSVP flow left users
  // guessing (they typed everything they saw and the button
  // stayed grey with no explanation - especially the "Activities
  // you'd love to do" chip group on step 2 and the intentions
  // grid on step 3).
  const stepMissing = useMemo(() => {
    const missing = [];
    if (step === 2) {
      if (!(f.about_me || '').trim()) missing.push('About you');
      else if ((f.about_me || '').trim().length < 30) {
        missing.push('About you (at least 30 characters)');
      }
      if (!(f.occupation || '').trim()) missing.push('Occupation');
      if (!f.languages || f.languages.length === 0) missing.push('Languages');
      if (!f.interests || f.interests.length === 0) missing.push('Interests');
      if (!f.activity_interests || f.activity_interests.length === 0) {
        missing.push('Activities you would love to do');
      }
    } else if (step === 3) {
      if (!f.intentions || f.intentions.length === 0) {
        missing.push('At least one intention');
      }
    }
    return missing;
  }, [step, f]);
  const [showStepHint, setShowStepHint] = useState(false);
  useEffect(() => { setShowStepHint(false); }, [step]);

  // C.4: prefill from authenticated user's profile. Runs once when the
  // event + user are both available. Does not override any resumed draft.
  // Bundle 168 · extends prefill to DOB (Bundle 167 top-level fallback),
  // photos, and occupation. Wizard IS the profile-completion flow, so
  // every wizard field that maps to profile pre-populates from profile.
  useEffect(() => {
    if (!isAuthenticated || !event || resumedDraft) return;
    const p = authUser?.profile || {};
    const dobPrefill = (p.dob || authUser?.dob || '').slice(0, 10);
    setF((prev) => ({
      ...prev,
      email: prev.email || authUser.email || '',
      phone: prev.phone || authUser.phone || '',
      full_name: prev.full_name || [authUser.first_name, authUser.last_name].filter(Boolean).join(' ') || '',
      preferred_first_name: prev.preferred_first_name || authUser.first_name || '',
      // Bundle 168 · DOB prefill with Bundle 167 top-level fallback.
      dob_or_age: prev.dob_or_age || dobPrefill,
      gender: prev.gender || p.gender || '',
      city: prev.city || (p.location || {}).city || '',
      state_region: prev.state_region || (p.location || {}).state || '',
      country: prev.country || (p.location || {}).country || prev.country,
      about_me: prev.about_me || p.about_me || p.bio || '',
      // Bundle 168 · occupation prefill (stays optional at submit).
      occupation: prev.occupation || p.occupation || '',
      languages: prev.languages?.length ? prev.languages : (p.languages || []),
      interests: prev.interests?.length ? prev.interests : (p.interests || []),
      activity_interests: prev.activity_interests?.length ? prev.activity_interests : (p.activities || []),
      intentions: prev.intentions?.length ? prev.intentions : (p.intentions || []),
      // Bundle 168 · photos prefill. Preserves any photos the user
      // already uploaded in the wizard (prev.photos wins). Normalises
      // profile.photos shape to `{id, url}` pairs the wizard expects.
      photos: prev.photos?.length
        ? prev.photos
        : (Array.isArray(p.photos) ? p.photos.map((x, i) => ({
            id: x?.id || `profile-${i}`,
            url: x?.url || x,
          })).filter((x) => x.url) : []),
      social_handles: {
        ...prev.social_handles,
        ...(p.social_handles || {}),
      },
    }));
  }, [isAuthenticated, event, authUser, resumedDraft]);

  const submit = async () => {
    setError('');
    setSubmitting(true);
    try {
      const answers = {
        full_name: f.full_name.trim(),
        preferred_first_name: f.preferred_first_name.trim(),
        dob_or_age: f.dob_or_age,
        gender: f.gender,
        city: f.city.trim(),
        state_region: f.state_region || null,
        country: f.country,
        about_me: f.about_me.trim(),
        languages: f.languages,
        occupation: f.occupation.trim() || '',
        interests: f.interests,
        activity_interests: f.activity_interests,
        intentions: f.intentions,
        marriage_prefs: f.intentions.includes('marriage') ? f.marriage_prefs : null,
        curated_seating_consent: !!f.curated_seating_consent,
        organizer_intro_consent: !!f.organizer_intro_consent,
        photos: f.photos.map(p => ({ id: p.id, url: p.url })),
        dietary_restrictions: f.dietary_restrictions.trim(),
        food_allergies: f.food_allergies.trim(),
        accessibility_request: f.accessibility_request.trim(),
        social_handles: f.social_handles,
        min_age_confirmation: f.min_age_confirmation,
        // Batch 4: how did you hear about this event?
        heard_from: f.heard_from || '',
        heard_from_other: f.heard_from === 'other' ? (f.heard_from_other || '').trim() : '',
        // Bundle 144 - mandatory Event T&C acceptance. Backend
        // returns 400 TERMS_REQUIRED if this is not true.
        terms_accepted: !!f.terms_accepted,
      };
      const { data } = await api.post(`/events/${slug}/rsvp`, {
        email: f.email,
        phone: f.phone,
        answers,
      });
      // Extract edit_token from edit_url (format: /rsvp/edit?t=<token>)
      let editToken = '';
      try {
        const u = new URL(data.edit_url, window.location.origin);
        editToken = u.searchParams.get('t') || '';
      } catch (_) { /* ignore */ }
      setResultUrls({
        edit_url: data.edit_url,
        verify_url: data.verify_url_fallback,
        verify_sent: data.verify_sent,
        // Bundle 151 - only surface "already RSVP'd" when the row
        // is truly done. Server returns duplicate:true only when
        // status='confirmed' AND payment_status='verified'.
        // Anything else (updated:true) advances to payment step.
        duplicate: (data.next_step === 'confirmation'
                       && data.duplicate === true),
        updated: !!data.updated,
        next_step: data.next_step || 'payment',
        // Bundle 176 - Fix A. Server-truth sub-panel hint for Step 6.
        // Values: verified | pending_verify | free | waived.
        payment_confirmation_state: data.payment_confirmation_state
          || (data.next_step === 'confirmation' ? 'verified' : 'unpaid'),
        registration_id: data.registration_id,
        edit_token: editToken,
      });
      // Batch 2 Part A: fire Lead pixel event on successful RSVP submit
      // (pre-payment). Safe no-op if no active pixels or vendor libs
      // haven't loaded yet.
      try {
        // Meta Advanced Matching: pass the freshly-submitted form values
        // (email/phone/name/dob/city/state/country/gender) so Meta can
        // resolve the event to a real customer identity. Plaintext here
        // is fine: fbevents.js hashes client-side, our backend hashes
        // before forwarding to CAPI, and neither surface persists it.
        const nameParts = (f.full_name || '').trim().split(/\s+/);
        const rsvpMatchData = {
          email: f.email,
          phone: f.phone,
          first_name: f.preferred_first_name || nameParts[0] || undefined,
          last_name: nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined,
          dob: /^\d{4}-\d{2}-\d{2}$/.test(f.dob_or_age || '') ? f.dob_or_age : undefined,
          gender: f.gender,
          city: f.city,
          state: f.state_region || undefined,
          country: f.country,
        };
        trackEvent('Lead', {
          content_name: event?.title || 'event',
          content_ids: [String(event?.id || slug || '')],
          content_category: 'rsvp',
        }, rsvpMatchData);
      } catch (_) { /* never break the flow */ }
      // Bug Z3: submission complete - clear the saved draft so
      // returning to the RSVP URL shows a fresh form (unless another
      // event's draft exists).
      clearDraft();
      // Bundle 151 - if server signaled confirmation state (fully
      // done), jump to the success screen. Otherwise (fresh submit
      // OR resubmit-with-merged-answers) advance to payment step.
      setStep((data.next_step === 'confirmation') ? 6 : 5);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Bundle 154 - map server-side mandate rejections to the
      // inline hint block so the user sees the exact required
      // group light up. Server codes: PHOTO_REQUIRED /
      // PHOTO_TOO_MANY / PHOTO_INVALID / SOCIAL_REQUIRED /
      // SOCIAL_TOO_MANY.
      const code = (detail && (detail.code || detail.error_code)) || '';
      if (['PHOTO_REQUIRED', 'PHOTO_TOO_MANY', 'PHOTO_INVALID',
              'SOCIAL_REQUIRED', 'SOCIAL_TOO_MANY'].includes(code)) {
        setShowMandateHint(true);
        setError(detail.message || 'Please complete the required fields.');
      } else if (detail?.missing_required_fields) {
        setError('Missing required fields: '
          + detail.missing_required_fields.join(', '));
      } else {
        // Bundle 160 - P0 hotfix. Prior behavior fell back to a
        // generic "Something went wrong" for every backend code
        // outside the Bundle 154 whitelist (TERMS_REQUIRED,
        // LAST_NAME_REQUIRED, NAME_INVALID, INCOMPLETE_PROFILE,
        // MINOR_NOT_ALLOWED, EVENT_ENDED, auth_required, and
        // FastAPI Pydantic 422 lists). Now we surface the exact
        // server-supplied `detail.message` via the shared
        // extractErrorMessage helper so users get an actionable
        // message instead of the ambiguous fallback.
        setError(extractErrorMessage(e,
          'Could not submit your RSVP. Please review your entries and try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-700" /></div>;
  if (!event) return <div className="p-16 text-center text-neutral-500">Event not available.</div>;

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 max-w-2xl">
      {/* Header */}
      <div className="mb-6">
        <Link to={`/events/${slug}`} className="text-sm text-neutral-500 hover:text-primary-800 inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to event
        </Link>
        <div className="text-xs font-semibold uppercase tracking-widest text-secondary-700 mb-1">RSVP · {event.title}</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-primary-900">Reserve your spot</h1>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-xs text-neutral-500 mb-2">
          <span>Step {step} of {TOTAL_STEPS}</span>
          <span>{Math.round((step / TOTAL_STEPS) * 100)}%</span>
        </div>
        <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-secondary-500 to-secondary-600 transition-all duration-500" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} />
        </div>
      </div>

      {/* Bug Z3: resumed-from-draft banner. Only shows once, on the step
          we hydrated into. Clearing the draft (via Start over) dismisses. */}
      {resumedDraft && step < 5 && (
        <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="rsvp-resumed-draft-banner">
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">We picked up where you left off.</div>
            <div className="text-xs text-amber-800 mt-0.5">Your answers were saved on this device. You can continue, or start over with a blank form.</div>
          </div>
          <Button variant="outline" size="sm" onClick={startOver} className="border-amber-400 text-amber-900 hover:bg-amber-100" data-testid="rsvp-start-over-btn">
            Start over
          </Button>
        </div>
      )}

      <Card className="border shadow-soft">
        <CardContent className="p-6 md:p-8">
          {/* C.4: authenticated summary banner. Shown on every step so
              the user knows their profile snapshot is being used. */}
          {isAuthenticated && (
            <div
              className="mb-6 rounded-lg border border-secondary-200 bg-secondary-50/60 px-4 py-3 flex items-center justify-between gap-3"
              data-testid="rsvp-authenticated-summary"
            >
              <div className="text-sm">
                <div className="font-semibold text-primary-900" data-testid="rsvp-authenticated-streamlined">
                  RSVPing as {authUser.first_name || authUser.email}
                </div>
                <div className="text-xs text-neutral-600 truncate">{authUser.email}</div>
              </div>
              <button
                type="button"
                className="text-xs text-primary-700 hover:underline"
                data-testid="rsvp-authenticated-not-me"
                onClick={async () => {
                  if (!window.confirm('Log out and continue as a guest?')) return;
                  await logout();
                  window.location.reload();
                }}
              >
                This isn&apos;t me
              </button>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-5">
              <StepHead icon={ShieldCheck} title="Essentials" subtitle="Just enough to get you in the door." />
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Full name" required hint="Enter both first and last name (e.g. Priya Sharma).">
                  <Input value={f.full_name} onChange={e => setF({ ...f, full_name: e.target.value })} data-testid="rsvp-full-name-input" />
                  {(() => {
                    // Bundle 160 - inline hint when full_name will
                    // trip the backend LAST_NAME_REQUIRED gate.
                    const raw = String(f.full_name || '').trim();
                    if (!raw) return null;
                    const parts = raw.split(/\s+/);
                    const tooShort = parts.length < 2
                      || parts[0].length < 2
                      || parts[parts.length - 1].length < 2;
                    if (!tooShort) return null;
                    return (
                      <div
                        className="mt-1 text-xs text-accent-700"
                        data-testid="rsvp-full-name-hint"
                      >
                        Enter your first AND last name (each at least 2 letters).
                      </div>
                    );
                  })()}
                </Field>
                <Field label="Preferred first name" required><Input value={f.preferred_first_name} onChange={e => setF({ ...f, preferred_first_name: e.target.value })} placeholder="How should we call you?" /></Field>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Date of birth" required><Input type="date" value={f.dob_or_age} onChange={e => setF({ ...f, dob_or_age: e.target.value })} /></Field>
                <Field label="Gender" required>
                  <Select value={f.gender} onValueChange={(v) => setF({ ...f, gender: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{GENDERS.map(g => <SelectItem key={g.v} value={g.v}>{g.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Country" required>
                  <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v, city: '' })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(c => <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="City" icon={MapPin} required>
                  <CityAutocomplete country={f.country} value={f.city} onChange={(v) => setF({ ...f, city: v })} />
                </Field>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="State or region" required hint="State, province, or region within your country.">
                  <Input
                    value={f.state_region}
                    onChange={(e) => setF({ ...f, state_region: e.target.value })}
                    placeholder="e.g. Texas, Maharashtra, Ontario"
                    data-testid="rsvp-state-input"
                    aria-required="true"
                  />
                </Field>
                <Field label="Email" icon={Mail} required><Input type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} /></Field>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Phone" icon={Phone} required hint="We derive the country code from your selected country above.">
                  <Input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })}
                         placeholder="(555) 123-4567"
                         data-testid="rsvp-phone-input" />
                </Field>
              </div>
              <label className="flex items-start gap-3 p-4 rounded-lg border border-neutral-200 bg-neutral-50/40 cursor-pointer">
                <Checkbox checked={f.min_age_confirmation} onCheckedChange={(v) => setF({ ...f, min_age_confirmation: !!v })} className="mt-0.5" />
                <span className="text-sm text-neutral-700">I confirm I am {minAge}+ years old and understand this is required to attend.</span>
              </label>

              {/* "How did you hear about this event?" - required for
                  every RSVP (guest + authenticated). Feeds the admin
                  analytics traffic-source panel. This replaced the
                  legacy C.4 `source_channel` question in Batch 4. */}
              <div className="pt-2 border-t border-neutral-200" data-testid="rsvp-heard-from-question">
                <Field label="How did you hear about this event?" required>
                  <Select value={f.heard_from} onValueChange={(v) => setF({ ...f, heard_from: v })}>
                    <SelectTrigger data-testid="rsvp-heard-from-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="facebook" data-testid="rsvp-heard-from-facebook">Facebook</SelectItem>
                      <SelectItem value="friend_or_word_of_mouth" data-testid="rsvp-heard-from-friend">Friend or word of mouth</SelectItem>
                      <SelectItem value="google_search" data-testid="rsvp-heard-from-google">Google search</SelectItem>
                      <SelectItem value="instagram" data-testid="rsvp-heard-from-instagram">Instagram</SelectItem>
                      <SelectItem value="meta_ad" data-testid="rsvp-heard-from-meta_ad">Meta ad</SelectItem>
                      <SelectItem value="reddit" data-testid="rsvp-heard-from-reddit">Reddit</SelectItem>
                      <SelectItem value="twitter_x" data-testid="rsvp-heard-from-twitter_x">Twitter / X</SelectItem>
                      <SelectItem value="whatsapp" data-testid="rsvp-heard-from-whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="other" data-testid="rsvp-heard-from-other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                {f.heard_from === 'other' && (
                  <div className="mt-2">
                    <Input
                      value={f.heard_from_other}
                      maxLength={200}
                      onChange={(e) => setF({ ...f, heard_from_other: e.target.value.slice(0, 200) })}
                      placeholder="Tell us how you heard (max 200 chars)"
                      data-testid="rsvp-heard-from-other-input"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <StepHead icon={Sparkles} title="About You" subtitle="Help the room get to know you." />
              <Field label="About you" required>
                <Textarea
                  rows={6}
                  value={f.about_me}
                  onChange={e => setF({ ...f, about_me: e.target.value })}
                  placeholder="Write in your own words. Anything on your mind, what you're excited about, what you'd like to find here, a little about yourself."
                />
                <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs text-neutral-500">Write it authentically first. Then let Dost polish it for you.</p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => (f.about_me || '').trim() && setDostOpen(true)}
                            disabled={!(f.about_me || '').trim()}
                            className="border-secondary-300 text-secondary-800 hover:bg-secondary-50 disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="rsvp-ask-dost-btn"
                          >
                            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Ask Dost to polish
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!(f.about_me || '').trim() && (
                        <TooltipContent side="left">
                          <span data-testid="rsvp-ask-dost-tooltip">Write a few sentences first, then Dost can polish them.</span>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </Field>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Languages" required>
                  <LanguagesTypeahead value={f.languages} onChange={(v) => setF({ ...f, languages: v })} />
                </Field>
                <Field label="Occupation" required>
                  <Input
                    value={f.occupation}
                    onChange={e => setF({ ...f, occupation: e.target.value })}
                    placeholder="e.g. Product Manager, Doctor, Student"
                    data-testid="rsvp-occupation-input"
                  />
                </Field>
              </div>
              <ChipGroup label="Interests" required options={intList} value={f.interests} onChange={(v) => setF({ ...f, interests: toggleChip(f.interests, v) })} />
              <ChipGroup label="Activities you'd love to do" required options={actList} value={f.activity_interests} onChange={(v) => setF({ ...f, activity_interests: toggleChip(f.activity_interests, v) })} />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <StepHead icon={Users} title="Intentions" subtitle="What kind of connection are you here for?" />
              <div className="grid gap-3 sm:grid-cols-2">
                {INTENTIONS.map(i => {
                  const on = f.intentions.includes(i.v);
                  return (
                    <button
                      key={i.v}
                      type="button"
                      onClick={() => setF({ ...f, intentions: toggleChip(f.intentions, i.v) })}
                      className={`text-left p-4 rounded-xl border transition-all ${on ? 'border-secondary-500 bg-gradient-to-br from-secondary-100 to-secondary-50 shadow-gold' : 'border-neutral-200 bg-white hover:border-secondary-300'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-primary-900">{i.label}</div>
                        {on && <Check className="h-4 w-4 text-secondary-700" />}
                      </div>
                      <div className="text-xs text-neutral-600">{i.desc}</div>
                    </button>
                  );
                })}
              </div>
              {f.intentions.includes('marriage') && (
                <div className="mt-4 p-4 rounded-xl border border-secondary-200 bg-secondary-50/40">
                  <div className="text-sm font-semibold text-primary-900 mb-3 flex items-center gap-2"><Heart className="h-4 w-4 text-secondary-700" /> Marriage-intro preferences (optional, private)</div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Preferred age range (min)"><Input type="number" value={f.marriage_prefs.age_min} onChange={e => setF({ ...f, marriage_prefs: { ...f.marriage_prefs, age_min: e.target.value } })} /></Field>
                    <Field label="Preferred age range (max)"><Input type="number" value={f.marriage_prefs.age_max} onChange={e => setF({ ...f, marriage_prefs: { ...f.marriage_prefs, age_max: e.target.value } })} /></Field>
                  </div>
                  <Field label="Religious / cultural notes (optional)"><Textarea rows={2} value={f.marriage_prefs.notes} onChange={e => setF({ ...f, marriage_prefs: { ...f.marriage_prefs, notes: e.target.value } })} placeholder="Only shared with organizers." /></Field>
                </div>
              )}
              <div className="mt-4 space-y-2 p-4 rounded-xl border border-neutral-200 bg-neutral-50/40">
                <div className="text-xs uppercase tracking-widest text-secondary-700 mb-1">Optional connection consents</div>
                <label className="flex items-start gap-2 text-sm text-neutral-700 cursor-pointer">
                  <input type="checkbox" checked={!!f.curated_seating_consent} onChange={(e) => setF({ ...f, curated_seating_consent: e.target.checked })} className="mt-1" data-testid="consent-seating" />
                  <span>I&apos;m open to being seated at a curated table with people who share my interests.</span>
                </label>
                <label className="flex items-start gap-2 text-sm text-neutral-700 cursor-pointer">
                  <input type="checkbox" checked={!!f.organizer_intro_consent} onChange={(e) => setF({ ...f, organizer_intro_consent: e.target.checked })} className="mt-1" data-testid="consent-intro" />
                  <span>I&apos;m open to organizer-brokered introductions during the event.</span>
                </label>
                <div className="text-[11px] text-neutral-500">Both are optional. You can update these later from your profile.</div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <StepHead icon={Camera} title="Your profile for this event" subtitle="Pictures and one social handle help organizers vet + seat you." />

              {/* Bundle 154 - Photos group. Required (at least 1, up
                  to 6). Copy is explicit that a single picture is
                  sufficient so users are not misled by "1 to 6". */}
              <div data-testid="rsvp-photos-group">
                <div className="text-xs uppercase tracking-widest text-secondary-700 mb-1 flex items-center gap-1.5">
                  Pictures <span className="text-accent-600">*</span>
                  <span className="ml-1 text-[10px] normal-case tracking-normal text-neutral-500 font-normal">
                    (at least 1 required, up to 6 allowed)
                  </span>
                </div>
                <PhotoGrid photos={f.photos} onChange={(photos) => setF({ ...f, photos })} uploadSession={uploadSession} />
                {showMandateHint && (!Array.isArray(f.photos) || f.photos.length < 1) && (
                  <div
                    className="mt-2 text-xs text-accent-700 bg-accent-50 border border-accent-200 rounded px-2 py-1 inline-block"
                    data-testid="rsvp-photo-required-msg"
                  >
                    Please add at least 1 profile picture.
                  </div>
                )}
              </div>

              {/* Bundle 154 - Social handles group. At least 1
                  handle across the 4 platforms is required. */}
              <div data-testid="rsvp-socials-group">
                <div className="text-xs uppercase tracking-widest text-secondary-700 mb-1 flex items-center gap-1.5">
                  Social media <span className="text-accent-600">*</span>
                  <span className="ml-1 text-[10px] normal-case tracking-normal text-neutral-500 font-normal">
                    (at least 1 required)
                  </span>
                </div>
                <p className="text-xs text-neutral-500 mb-3">Private - visible only to authorized organizers.</p>
                <SocialInput label="Instagram" prefix="@" value={f.social_handles.instagram} onChange={(v) => setF({ ...f, social_handles: { ...f.social_handles, instagram: v } })} buildUrl={(h) => `https://instagram.com/${h.replace(/^@/, '')}`} />
                <SocialInput label="LinkedIn" prefix="linkedin.com/in/" value={f.social_handles.linkedin} onChange={(v) => setF({ ...f, social_handles: { ...f.social_handles, linkedin: v } })} buildUrl={(h) => `https://linkedin.com/in/${h}`} />
                <SocialInput label="TikTok" prefix="@" value={f.social_handles.tiktok} onChange={(v) => setF({ ...f, social_handles: { ...f.social_handles, tiktok: v } })} buildUrl={(h) => `https://tiktok.com/@${h.replace(/^@/, '')}`} />
                <SocialInput label="Facebook" prefix="facebook.com/" value={f.social_handles.facebook} onChange={(v) => setF({ ...f, social_handles: { ...f.social_handles, facebook: v } })} buildUrl={(h) => `https://facebook.com/${h}`} />
                {showMandateHint && !anySocialFilled && (
                  <div
                    className="mt-2 text-xs text-accent-700 bg-accent-50 border border-accent-200 rounded px-2 py-1 inline-block"
                    data-testid="rsvp-social-required-msg"
                  >
                    Please add at least 1 social handle.
                  </div>
                )}
              </div>

              {/* Bundle 154 - Optional details group. Kept clearly
                  separate from the required blocks above so users
                  see exactly what is + is not mandatory. */}
              <div data-testid="rsvp-optional-group" className="pt-2 border-t border-neutral-100">
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
                  Optional details
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Dietary restrictions"><Textarea rows={2} value={f.dietary_restrictions} onChange={e => setF({ ...f, dietary_restrictions: e.target.value })} placeholder="e.g. vegetarian, halal" /></Field>
                  <Field label="Food allergies (flagged separately)"><Textarea rows={2} value={f.food_allergies} onChange={e => setF({ ...f, food_allergies: e.target.value })} placeholder="e.g. nuts, shellfish" /></Field>
                </div>
                <div className="mt-4">
                  <Field label="Accessibility request"><Input value={f.accessibility_request} onChange={e => setF({ ...f, accessibility_request: e.target.value })} placeholder="Anything we can do to make you comfortable?" /></Field>
                </div>
              </div>

              {/* Bundle 144 - mandatory Event T&C checkbox. Sits
                  directly above the "Continue to payment" nav
                  button which is the effective RSVP-submit action.
                  Backend rejects submits without terms_accepted
                  with 400 code TERMS_REQUIRED. */}
              <div
                className="pt-4 mt-2 border-t border-neutral-200"
                data-testid="rsvp-terms-block"
              >
                <label className="flex items-start gap-3 p-4 rounded-lg border border-secondary-200 bg-secondary-50/40 cursor-pointer">
                  <Checkbox
                    checked={f.terms_accepted}
                    onCheckedChange={(v) => setF({ ...f, terms_accepted: !!v })}
                    className="mt-0.5"
                    data-testid="rsvp-terms-checkbox"
                  />
                  <span className="text-sm text-primary-800">
                    I agree to the{' '}
                    <a
                      href="/terms#event-photography-video"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-secondary-800 hover:text-secondary-900"
                      data-testid="rsvp-terms-link"
                    >
                      Event Terms &amp; Conditions
                    </a>
                    .
                  </span>
                </label>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <StepHead icon={CreditCard} title="Payment" subtitle="Choose how you'd like to pay." />
              {resultUrls?.duplicate ? (
                <div className="p-4 rounded-lg bg-secondary-50 border border-secondary-200 text-sm text-primary-900">
                  You&apos;ve already RSVP&apos;d for this event. We just re-sent your private edit link so you can update your details.
                  <div className="pt-3">
                    <Button variant="outline" onClick={() => setStep(6)}>Continue</Button>
                  </div>
                </div>
              ) : (
                <PaymentStep
                  registrationId={resultUrls?.registration_id}
                  editToken={resultUrls?.edit_token}
                  eventId={event?.id}
                  eventSlug={event?.slug}
                  eventTitle={event?.title}
                  onPaymentInitiated={() => { /* redirect happens in child */ }}
                  onManualDone={() => setStep(6)}
                />
              )}
            </div>
          )}

          {step === 6 && (
            <div className="space-y-5 text-center py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-secondary-100 to-secondary-50 border border-secondary-200 shadow-gold mx-auto">
                <Check className="h-7 w-7 text-secondary-700" />
              </div>
              {resultUrls?.duplicate
                && resultUrls?.payment_confirmation_state === 'pending_verify' ? (
                <>
                  <h2
                    className="font-display text-2xl font-bold text-primary-900"
                    data-testid="rsvp-pending-verify-heading"
                  >
                    Payment received, awaiting confirmation.
                  </h2>
                  <p
                    className="text-neutral-600"
                    data-testid="rsvp-pending-verify-copy"
                  >
                    Thanks for marking your payment. We&apos;ll email your
                    ticket as soon as the host confirms it.
                  </p>
                  {resultUrls?.profile_action_required ? (
                    <div
                      className="mt-6 pt-6 border-t border-neutral-200 space-y-3 text-left"
                      data-testid="rsvp-complete-profile-block"
                    >
                      <p className="text-xs font-semibold uppercase tracking-wider text-secondary-700">
                        One more thing
                      </p>
                      <p className="text-neutral-700">
                        Complete your profile to receive your ticket.
                      </p>
                      <Button
                        type="button"
                        onClick={() => {
                          const target = Number(
                            resultUrls?.resume_profile_step || 1);
                          setStep(target >= 1 && target <= 4 ? target : 1);
                        }}
                        className="bg-primary-800 hover:bg-primary-900 text-white"
                        data-testid="rsvp-resume-profile-btn"
                      >
                        Resume profile
                      </Button>
                    </div>
                  ) : null}
                  <div className="pt-3">
                    <button
                      type="button"
                      onClick={() => setShowRetryPaymentModal(true)}
                      className="text-xs text-neutral-500 underline hover:text-neutral-700"
                      data-testid="rsvp-retry-payment-link"
                    >
                      I didn&apos;t actually pay - retry payment
                    </button>
                  </div>
                </>
              ) : resultUrls?.duplicate ? (
                <>
                  <h2 className="font-display text-2xl font-bold text-primary-900">You&apos;ve already RSVP&apos;d</h2>
                  <p className="text-neutral-600">We just re-sent your private edit link so you can update your details anytime.</p>
                </>
              ) : (
                <>
                  <h2 className="font-display text-2xl font-bold text-primary-900">Thanks - you&apos;re almost in.</h2>
                  <p className="text-neutral-600">
                    {resultUrls?.verify_sent
                      ? `We sent a verification link to ${f.email}. Click it to confirm your spot.`
                      : `Email transport isn't live yet in this environment - use the link below to verify.`}
                  </p>
                </>
              )}
              {resultUrls?.verify_url && !resultUrls?.verify_sent && (
                <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-left">
                  <div className="text-xs text-yellow-800 font-semibold uppercase tracking-widest mb-1">Testing link (email not sent)</div>
                  <a href={resultUrls.verify_url} className="text-sm text-primary-800 break-all underline">{resultUrls.verify_url}</a>
                </div>
              )}
              {/*
                Bundle 55 - NUKED the legacy anonymous-friendly success block.
                The prior UI here rendered a "save profile / continue as guest"
                fork with a magic edit-link URL. Bundle 51 already server-side
                enforces auth on RSVP submit (401 for anon), so anon callers
                never reach this render. And Bundle 54 nuked the magic-edit
                link from emails; showing it on the success page defeated
                that fix. Authenticated users now get a clean next-steps
                message routed at /my-events.
              */}
              {isAuthenticated ? (
                <div className="pt-4 border-t border-neutral-100 text-left">
                  <div className="text-sm font-semibold text-primary-900 mb-2" data-testid="rsvp-success-next-steps">
                    Next steps
                  </div>
                  <p className="text-sm text-neutral-600 leading-relaxed mb-3">
                    We saved your RSVP to your Dostination account. Once you verify
                    your email, you can view or edit your RSVP from
                    {' '}<strong>My Events</strong> anytime.
                  </p>
                  <Button
                    onClick={() => navigate('/my-events')}
                    className="bg-primary-800 hover:bg-primary-900 text-white"
                    data-testid="rsvp-success-my-events-btn"
                  >
                    Go to My Events
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          {error && <div className="mt-4 text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-lg p-3">{error}</div>}

          {/* Bundle 176 - Task 3. Retry Payment confirmation modal. */}
          {showRetryPaymentModal && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
              role="dialog"
              aria-modal="true"
              data-testid="rsvp-retry-payment-modal"
            >
              <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                <h3 className="font-display text-lg font-bold text-primary-900">
                  Reset your payment state?
                </h3>
                <p className="mt-2 text-sm text-neutral-600">
                  This will reset your payment state. You&apos;ll be sent
                  back to the payment options. Continue?
                </p>
                {retryPaymentError && (
                  <div
                    className="mt-3 rounded-md border border-accent-200 bg-accent-50 p-3 text-sm text-accent-700"
                    data-testid="rsvp-retry-payment-error"
                  >
                    {retryPaymentError}
                  </div>
                )}
                <div className="mt-5 flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowRetryPaymentModal(false);
                      setRetryPaymentError('');
                    }}
                    disabled={retryPaymentLoading}
                    data-testid="rsvp-retry-payment-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      setRetryPaymentLoading(true);
                      setRetryPaymentError('');
                      try {
                        await api.post(
                          `/rsvp/${resultUrls?.registration_id}/reset-payment-intent`,
                          {},
                        );
                        setShowRetryPaymentModal(false);
                        setResultUrls((prev) => (prev ? {
                          ...prev,
                          duplicate: false,
                          next_step: 'payment',
                          payment_confirmation_state: 'unpaid',
                        } : prev));
                        setStep(5);
                      } catch (e) {
                        const d = e?.response?.data?.detail;
                        setRetryPaymentError(
                          (d && (d.message || d.error_code))
                            || 'Could not reset payment. Please contact the host.'
                        );
                      } finally {
                        setRetryPaymentLoading(false);
                      }
                    }}
                    disabled={retryPaymentLoading}
                    className="bg-primary-800 hover:bg-primary-900 text-white"
                    data-testid="rsvp-retry-payment-confirm"
                  >
                    {retryPaymentLoading ? 'Resetting...' : 'Yes, reset'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Nav */}
          {step < 6 && (
            <div className="mt-8 pt-6 border-t border-neutral-100 space-y-3">
              {/* Bundle 162 - inline hint block. When Continue is
                  disabled AND the user clicked the click-catcher
                  overlay, we surface the exact list of missing
                  fields so they can act on it. */}
              {showStepHint && !stepIsValid && stepMissing.length > 0 ? (
                <div
                  className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                  data-testid="rsvp-step-missing-hint"
                >
                  <div className="font-semibold mb-1">
                    A couple of fields still need attention:
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {stepMissing.map((m) => (
                      <li key={m} data-testid={`rsvp-step-missing-${m.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{m}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="flex items-center justify-between">
                <Button variant="ghost" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1 || submitting}>
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                {step < 4 && (
                  <div className="relative">
                    <Button
                      onClick={() => { setShowStepHint(false); setStep(s => s + 1); }}
                      disabled={!stepIsValid}
                      className="bg-primary-800 hover:bg-primary-900 text-white"
                      data-testid={`rsvp-step-${step}-next-btn`}
                    >
                      Next <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                    {!stepIsValid ? (
                      <button
                        type="button"
                        onClick={() => setShowStepHint(true)}
                        className="absolute inset-0 cursor-pointer bg-transparent"
                        aria-label="Show missing fields"
                        data-testid={`rsvp-step-${step}-hint-trigger`}
                      />
                    ) : null}
                  </div>
                )}
                {step === 4 && (
                  <div className="relative">
                    {/* Bundle 154 - transparent click-catcher over
                        the disabled Continue button. When the
                        mandate is unmet, clicking anywhere in the
                        button area surfaces the inline hint badges
                        while keeping the visual disabled state per
                        product spec. */}
                    <Button
                      onClick={submit}
                      disabled={submitting || !stepIsValid}
                      className="bg-primary-800 hover:bg-primary-900 text-white"
                      data-testid="rsvp-submit-btn"
                    >
                      {submitting ? 'Submitting…' : <>Continue to payment <ArrowRight className="h-4 w-4 ml-2" /></>}
                    </Button>
                    {!submitting && !stepIsValid && (
                      <button
                        type="button"
                        onClick={() => setShowMandateHint(true)}
                        className="absolute inset-0 cursor-pointer bg-transparent"
                        aria-label="Show missing requirements"
                        data-testid="rsvp-submit-hint-trigger"
                    />
                  )}
                </div>
              )}
              {step === 5 && resultUrls?.duplicate && (
                <Button onClick={() => setStep(6)} className="bg-primary-800 hover:bg-primary-900 text-white">
                  Continue <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <DostPolishModal
        open={dostOpen}
        onClose={() => setDostOpen(false)}
        originalText={f.about_me}
        uploadSessionToken={uploadSession}
        regId={resultUrls?.registration_id || null}
        onAccept={(polished) => setF((cur) => ({ ...cur, about_me: polished }))}
      />
    </div>
  );
};

const StepHead = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-2">
    <div className="flex items-center gap-3 mb-1">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary-100 to-secondary-50 border border-secondary-200 flex items-center justify-center">
        <Icon className="h-5 w-5 text-secondary-700" />
      </div>
      <h2 className="font-display text-2xl font-bold text-primary-900">{title}</h2>
    </div>
    <p className="text-sm text-neutral-600">{subtitle}</p>
  </div>
);

const Field = ({ label, icon: Icon, required, hint, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-secondary-700 mb-1 flex items-center gap-1.5">
      {Icon && <Icon className="h-3 w-3" />} {label} {required && <span className="text-accent-600">*</span>}
    </Label>
    <div>{children}</div>
    {hint && <div className="mt-1 text-[11px] text-neutral-500">{hint}</div>}
  </div>
);

const ChipGroup = ({ label, required, options, value, onChange }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-secondary-700 mb-2 block">
      {label} {required && <span className="text-accent-600">*</span>}
    </Label>
    <div className="flex flex-wrap gap-2">
      {options.map(o => {
        const on = value.includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${on ? 'bg-gradient-to-r from-secondary-100 to-secondary-50 border-secondary-200 text-primary-800 shadow-sm' : 'bg-white border-neutral-200 text-neutral-600 hover:border-secondary-300'}`}
          >
            {o}
          </button>
        );
      })}
    </div>
  </div>
);

const PhotoGrid = ({ photos, onChange, uploadSession }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const canAddMore = photos.length < 6;

  const onFile = async (e) => {
    setErr('');
    if (!uploadSession) {
      setErr('Upload session not ready. Please refresh the page.');
      return;
    }
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    if (!files.length) return;
    setUploading(true);
    const nextPhotos = [...photos];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('owner_type', 'guest_registration');
        fd.append('is_public', 'false'); // Private by default; signed URL returned.
        fd.append('upload_session_token', uploadSession);
        const { data } = await api.post('/media/upload/v2', fd);
        nextPhotos.push({ id: data.id, url: data.url, thumb_url: data.thumb_url });
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Upload failed');
      }
    }
    onChange(nextPhotos);
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {photos.map((p, i) => (
          <div key={p.id} className="relative aspect-square rounded-xl overflow-hidden border border-neutral-200">
            <img src={p.thumb_url || p.url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(photos.filter(x => x.id !== p.id))}
              className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-full p-1 shadow-sm"
              aria-label="Remove"
            ><X className="h-3 w-3 text-accent-700" /></button>
          </div>
        ))}
        {canAddMore && (
          <label className={`aspect-square rounded-xl border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center text-neutral-500 text-xs cursor-pointer hover:border-secondary-400 hover:text-secondary-700 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            <Upload className="h-5 w-5 mb-1" />
            <span>{uploading ? 'Uploading…' : 'Add photo'}</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={onFile} />
          </label>
        )}
      </div>
      {err && <div className="text-xs text-accent-700 mt-2">{err}</div>}
      <p className="text-xs text-neutral-500 mt-2">Up to 6 photos. JPG / PNG / WebP up to 5 MB each.</p>
    </div>
  );
};


// --- Phase 4.5-A helpers ---

const CityAutocomplete = ({ country, value, onChange }) => {
  const countryCode = (COUNTRIES.find(c => c.name === country) || {}).code || 'US';
  const list = CITIES[countryCode] || [];
  const [open, setOpen] = useState(false);
  const [showFreeTextHint, setShowFreeTextHint] = useState(false);
  const q = (value || '').toLowerCase();
  const suggestions = q.length >= 1
    ? list.filter(c => c.toLowerCase().includes(q)).slice(0, 8)
    : [];

  // Debounce: if the user has typed something but nothing matches after
  // ~500ms of idle, surface a friendly "we'll use what you typed" hint.
  useEffect(() => {
    if (!q || suggestions.length > 0) {
      setShowFreeTextHint(false);
      return;
    }
    const t = setTimeout(() => setShowFreeTextHint(true), 500);
    return () => clearTimeout(t);
  }, [q, suggestions.length]);

  return (
    <div className="relative">
      <Input
        value={value}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        placeholder={list.length ? 'Start typing your city (any city is accepted)' : 'Enter your city'}
        data-testid="rsvp-city-input"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-neutral-200 bg-white shadow-lg">
          {suggestions.map(s => (
            <button key={s} type="button" onMouseDown={() => onChange(s)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-secondary-50">
              {s}
            </button>
          ))}
        </div>
      )}
      {showFreeTextHint && q && suggestions.length === 0 && (
        <div className="mt-1 text-[11px] text-secondary-800" data-testid="rsvp-city-freetext-hint">
          Not in the list? That&apos;s fine - we&apos;ll use what you typed.
        </div>
      )}
    </div>
  );
};

const LanguagesTypeahead = ({ value, onChange }) => {
  const [q, setQ] = useState('');
  const suggestions = q.length >= 1
    ? LANG_LIST.filter(l => l.toLowerCase().includes(q.toLowerCase()) && !value.includes(l)).slice(0, 8)
    : [];
  const add = (l) => { onChange([...value, l]); setQ(''); };
  const remove = (l) => onChange(value.filter(x => x !== l));
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map(l => (
          <span key={l} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-secondary-100 to-secondary-50 border border-secondary-200 text-primary-800">
            {l}
            <button type="button" onClick={() => remove(l)} className="text-neutral-500 hover:text-accent-700"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="relative">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Start typing a language" />
        {suggestions.length > 0 && (
          <div className="absolute z-20 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-neutral-200 bg-white shadow-lg">
            {suggestions.map(s => (
              <button key={s} type="button" onMouseDown={() => add(s)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-secondary-50">{s}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

import { Instagram, Linkedin, Facebook, ExternalLink, Music2 } from 'lucide-react';
const PLATFORM_ICON = { Instagram: Instagram, LinkedIn: Linkedin, TikTok: Music2, Facebook: Facebook };
const SocialInput = ({ label, prefix, value, onChange, buildUrl }) => {
  const Icon = PLATFORM_ICON[label] || Instagram;
  const url = value ? buildUrl(value) : '';
  return (
    <div className="mb-3">
      <Label className="text-xs uppercase tracking-widest text-secondary-700 mb-1 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </Label>
      <div className="flex items-stretch rounded-lg border border-neutral-200 bg-white overflow-hidden focus-within:border-secondary-400">
        <span className="px-3 flex items-center text-sm text-neutral-500 bg-neutral-50 border-r border-neutral-200">{prefix}</span>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/^@/, ''))}
          placeholder="handle"
          className="flex-1 px-3 py-2 text-sm outline-none bg-transparent"
        />
        {value && (
          <a href={url} target="_blank" rel="noopener noreferrer"
             className="px-3 flex items-center text-primary-700 hover:text-primary-900 border-l border-neutral-200"
             title="Open link">
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
};

export default RsvpMultiStepPage;
